import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Save, User, MapPin, Globe, Lock, AlertCircle, Check, Loader, Camera, Package, Briefcase, X, FileText
} from 'lucide-react';
import {
    getRecruiterProfile,
    updateRecruiterProfile,
    changePassword,
    getWilayas,


    getCommunesOfWilaya,
    uploadProfilePicture
} from '../../utils/Api';
import api from '../../utils/Api';
import ImageCropperModal from '../../components/common/ImageCropperModal';

const Settings = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [sectors, setSectors] = useState([]);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Form States
    const [formData, setFormData] = useState({
        company_name: '',
        employees_count: '',
        sector_id: '',
        custom_sector: '',
        description: '',
        website: '',
        phone_number: '',
        address: {
            line1: '',
            line2: '',
            zip_code: '',
            city: '',
            wilaya: '',
            coordinates: []
        },
        social_links: {
            linkedin: '',
            facebook: '',
            instagram: ''
        },
        profile_picture: ''
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // Location Autocomplete State
    const [wilayas, setWilayas] = useState([]);
    const [communes, setCommunes] = useState([]);
    const [showWilayaDropdown, setShowWilayaDropdown] = useState(false);
    const [wilayaDisplayName, setWilayaDisplayName] = useState('');

    // Image Upload State
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [isCropperOpen, setIsCropperOpen] = useState(false);
    const [tempImageUrl, setTempImageUrl] = useState(null);

    // Subscription State
    const [subscriptions, setSubscriptions] = useState([]);
    const [usageStats, setUsageStats] = useState(null);
    const [cvUsage, setCvUsage] = useState(0);

    useEffect(() => {
        fetchProfile();
        fetchSectors();
    }, []);

    const fetchSectors = async () => {
        try {
            const res = await api.get('/sector');
            setSectors(res.data || []);
        } catch (err) {
            console.error("Error fetching sectors:", err);
        }
    };

    const fetchProfile = async () => {
        try {
            const res = await getRecruiterProfile();
            const recruiter = res.recruiter;

            setFormData({
                company_name: recruiter.company_name || '',
                employees_count: recruiter.employees_count || '', // Added
                sector_id: recruiter.sector?._id || (recruiter.custom_sector ? 'Autre' : ''),
                custom_sector: recruiter.custom_sector || '',
                description: recruiter.description || '',
                website: recruiter.website || '',
                phone_number: recruiter.phone_number || '',
                address: {
                    line1: recruiter.address?.line1 || '',
                    line2: recruiter.address?.line2 || '',
                    zip_code: recruiter.address?.zip_code || '',
                    city: recruiter.address?.city || '',
                    wilaya: recruiter.address?.wilaya || '',
                    coordinates: recruiter.address?.coordinates || []
                },
                social_links: {
                    linkedin: recruiter.social_links?.linkedin || '',
                    facebook: recruiter.social_links?.facebook || '',
                    instagram: recruiter.social_links?.instagram || ''
                },
                profile_picture: recruiter.profile_picture || ''
            });

            if (recruiter.profile_picture) {
                if (recruiter.profile_picture.startsWith('http')) {
                    setImagePreview(recruiter.profile_picture);
                } else {
                    // Fetch via API
                    try {
                        const pathParts = recruiter.profile_picture.split('/');
                        const filename = pathParts.pop();
                        const folder = pathParts.length > 0 ? pathParts.join('/') : 'profile_pictures';

                        const response = await api.get(`/files/${folder}/${filename}`, {
                            responseType: 'blob',
                        });

                        const blob = new Blob([response.data], { type: response.headers['content-type'] });
                        const objectUrl = window.URL.createObjectURL(blob);
                        setImagePreview(objectUrl);
                    } catch (err) {
                        console.error("Error fetching image blob", err);
                    }
                }
            }

            // Pre-fetch communes if wilaya exists (it is an ID now)
            if (recruiter.address?.wilaya) {
                try {
                    // Fetch wilaya details by ID/Code to get the name
                    const wilayaRes = await getWilayas(recruiter.address.wilaya);
                    const matchedWilaya = wilayaRes.find(w =>
                        w.id === recruiter.address.wilaya ||
                        w.code === recruiter.address.wilaya ||
                        w.name_fr.toLowerCase() === recruiter.address.wilaya.toLowerCase() ||
                        w.name_ar === recruiter.address.wilaya ||
                        w.name === recruiter.address.wilaya
                    );

                    if (matchedWilaya) {
                        setWilayaDisplayName(matchedWilaya.name_fr || matchedWilaya.name);
                        const communesRes = await getCommunesOfWilaya(matchedWilaya.code);
                        setCommunes(communesRes);
                    } else {
                        // Fallback if not found (or if it was legacy name data)
                        setWilayaDisplayName(recruiter.address.wilaya);
                    }
                } catch (error) {
                    console.error("Error loading location details", error);
                    setWilayaDisplayName(recruiter.address.wilaya);
                }
            }

            // Set Subscription & Fetch Limits
            try {
                const subRes = await api.get('/recruiter/subscriptions');
                setSubscriptions(subRes.data.subscriptions || []);
            } catch (err) {
                console.error("Error fetching subscriptions", err);
            }

            try {
                const limitRes = await api.get('/jobOffer/limits');
                if (limitRes.data.success) {
                    setUsageStats(limitRes.data);
                }
            } catch (err) {
                console.error("Error fetching limits", err);
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
            setMessage({ type: 'error', text: "Erreur lors du chargement du profil" });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e, section = null) => {
        const { name, value } = e.target;
        if (section) {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [name]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Location Handlers
    const handleWilayaChange = async (e) => {
        const value = e.target.value;
        setWilayaDisplayName(value);

        // Reset stored ID when typing
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, wilaya: '', city: '', zip_code: '', coordinates: [] }
        }));
        setCommunes([]);

        if (value.length > 0) {
            try {
                const res = await getWilayas(value);
                setWilayas(res);
                setShowWilayaDropdown(true);
            } catch (error) {
                console.error("Error fetching wilayas:", error);
            }
        } else {
            setWilayas([]);
            setShowWilayaDropdown(false);
        }
    };

    const selectWilaya = async (wilaya) => {
        setWilayaDisplayName(wilaya.name_fr || wilaya.name);
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                wilaya: wilaya.id,
                city: '',
                zip_code: '',
                coordinates: [Number(wilaya.latitude), Number(wilaya.longitude)]
            }
        }));
        setWilayas([]);
        setShowWilayaDropdown(false);

        try {
            const res = await getCommunesOfWilaya(wilaya.code);
            setCommunes(res);
        } catch (error) {
            console.error("Error fetching communes:", error);
        }
    };

    const handleCitySelect = (e) => {
        const cityName = e.target.value;
        const selectedCommune = communes.find(c => c.name === cityName);

        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                city: cityName,
                zip_code: selectedCommune ? selectedCommune.postalCode : prev.address.zip_code,
                coordinates: selectedCommune ? [Number(selectedCommune.latitude), Number(selectedCommune.longitude)] : prev.address.coordinates
            }
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setTempImageUrl(reader.result);
            setIsCropperOpen(true);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCropComplete = async (croppedBlob) => {
        const previewUrl = URL.createObjectURL(croppedBlob);
        setImagePreview(previewUrl);

        const file = new File([croppedBlob], "logo.jpg", { type: "image/jpeg" });

        // Upload
        const uploadFormData = new FormData();
        uploadFormData.append('profile_picture', file);

        setUploadingImage(true);
        setIsCropperOpen(false);
        setTempImageUrl(null);

        try {
            const res = await uploadProfilePicture(uploadFormData);
            setFormData(prev => ({ ...prev, profile_picture: res.fileName }));
            setMessage({ type: 'success', text: "Photo de profil mise à jour" });
        } catch (error) {
            console.error("Error uploading image:", error);
            setMessage({ type: 'error', text: "Erreur lors du téléchargement de l'image" });
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            if (activeTab === 'security') {
                if (passwordData.newPassword !== passwordData.confirmPassword) {
                    throw new Error("Les mots de passe ne correspondent pas");
                }
                await changePassword({
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                });
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                await updateRecruiterProfile(formData);
            }
            setMessage({ type: 'success', text: "Modifications enregistrées avec succès" });
        } catch (error) {
            console.error("Error saving settings:", error);
            setMessage({
                type: 'error',
                text: error.response?.data?.message || error.message || "Erreur lors de l'enregistrement"
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="w-8 h-8 animate-spin text-primary-600 dark:text-gray-200" />
            </div>
        );
    }

    const tabs = [
        { id: 'general', label: 'Général', icon: User },
        { id: 'address', label: 'Adresse', icon: MapPin },
        { id: 'social', label: 'Réseaux Sociaux', icon: Globe },
        { id: 'subscription', label: 'Abonnement', icon: Package },
        { id: 'security', label: 'Sécurité', icon: Lock },
    ];

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Paramètres</h1>

            {message.text && (
                <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${message.type === 'success'
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                    {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    {message.text}
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                                ? 'text-primary-600 dark:text-gray-200 border-b-2 border-primary-600 dark:border-gray-200 bg-primary-50/50 dark:bg-primary-900/10'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="p-6">
                    <form onSubmit={handleSubmit}>
                        {/* General Tab */}
                        {activeTab === 'general' && (
                            <div className="space-y-6">
                                {/* Profile Picture Upload */}
                                <div className="flex items-center space-x-6">
                                    <div className="relative">
                                        <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 dark:border-gray-700">
                                            {imagePreview ? (
                                                <img
                                                    src={imagePreview}
                                                    alt="Profile"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                    <User className="w-12 h-12" />
                                                </div>
                                            )}
                                            {uploadingImage && (
                                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                    <Loader className="w-6 h-6 text-white animate-spin" />
                                                </div>
                                            )}
                                        </div>
                                        <label
                                            htmlFor="profile-upload"
                                            className="absolute bottom-0 right-0 p-1.5 bg-primary-600 text-white rounded-full cursor-pointer hover:bg-primary-700 transition-colors shadow-sm"
                                        >
                                            <Camera className="w-4 h-4" />
                                            <input
                                                id="profile-upload"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleImageChange}
                                                disabled={uploadingImage}
                                            />
                                        </label>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Logo de l'entreprise</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            JPG, GIF ou PNG. Taille max 2MB.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Nom de l'entreprise
                                        </label>
                                        <input
                                            type="text"
                                            name="company_name"
                                            value={formData.company_name}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                    {/* Added Employee Count Select */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Taille de l'entreprise
                                        </label>
                                        <select
                                            name="employees_count"
                                            value={formData.employees_count}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="">Non spécifié</option>
                                            <option value="1-10">1-10 employés</option>
                                            <option value="11-50">11-50 employés</option>
                                            <option value="51-200">51-200 employés</option>
                                            <option value="201-500">201-500 employés</option>
                                            <option value="500+">500+ employés</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Secteur d'activité
                                        </label>
                                        <select
                                            name="sector_id"
                                            value={formData.sector_id}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    sector_id: val,
                                                    custom_sector: val === 'Autre' ? prev.custom_sector : ''
                                                }));
                                            }}
                                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="">Sélectionnez un secteur</option>
                                            {sectors.map(sector => (
                                                <option key={sector._id} value={sector._id}>
                                                    {sector.name}
                                                </option>
                                            ))}
                                            <option value="Autre">Autre</option>
                                        </select>
                                    </div>

                                    {formData.sector_id === 'Autre' && (
                                        <div className="animate-in slide-in-from-top-2 duration-200">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Précisez le secteur *
                                            </label>
                                            <input
                                                type="text"
                                                name="custom_sector"
                                                value={formData.custom_sector}
                                                onChange={handleInputChange}
                                                placeholder="Tapez votre secteur"
                                                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Site Web
                                        </label>
                                        <input
                                            type="url"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Téléphone
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone_number"
                                            value={formData.phone_number}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={4}
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Address Tab */}
                        {activeTab === 'address' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Wilaya
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={wilayaDisplayName}
                                                onChange={handleWilayaChange}
                                                placeholder="Rechercher une wilaya..."
                                                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                            />
                                            {showWilayaDropdown && wilayas.length > 0 && (
                                                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                    {wilayas.map((wilaya) => (
                                                        <button
                                                            key={wilaya.code}
                                                            type="button"
                                                            onClick={() => selectWilaya(wilaya)}
                                                            className="w-full px-4 py-2 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition flex items-center justify-between text-sm text-gray-900 dark:text-white"
                                                        >
                                                            {wilaya.code} - {wilaya.name_fr || wilaya.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Ville
                                        </label>
                                        <input
                                            type="text"
                                            list="settings-cities-list"
                                            value={formData.address.city}
                                            onChange={handleCitySelect}
                                            disabled={!formData.address.wilaya}
                                            placeholder={!formData.address.wilaya ? "Sélectionnez d'abord une wilaya" : "Sélectionnez ou tapez une ville"}
                                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                                        />
                                        <datalist id="settings-cities-list">
                                            {communes.map((commune) => (
                                                <option key={commune.id || Math.random()} value={commune.name} />
                                            ))}
                                        </datalist>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Code Postal
                                        </label>
                                        <input
                                            type="text"
                                            name="zip_code"
                                            value={formData.address.zip_code}
                                            onChange={(e) => handleInputChange(e, 'address')}
                                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                            placeholder="Code postal"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Adresse (Ligne 1)
                                    </label>
                                    <input
                                        type="text"
                                        name="line1"
                                        value={formData.address.line1}
                                        onChange={(e) => handleInputChange(e, 'address')}
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Adresse (Ligne 2)
                                    </label>
                                    <input
                                        type="text"
                                        name="line2"
                                        value={formData.address.line2}
                                        onChange={(e) => handleInputChange(e, 'address')}
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Social Tab */}
                        {activeTab === 'social' && (
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        LinkedIn
                                    </label>
                                    <input
                                        type="url"
                                        name="linkedin"
                                        value={formData.social_links.linkedin}
                                        onChange={(e) => handleInputChange(e, 'social_links')}
                                        placeholder="https://linkedin.com/company/..."
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Facebook
                                    </label>
                                    <input
                                        type="url"
                                        name="facebook"
                                        value={formData.social_links.facebook}
                                        onChange={(e) => handleInputChange(e, 'social_links')}
                                        placeholder="https://facebook.com/..."
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Instagram
                                    </label>
                                    <input
                                        type="url"
                                        name="instagram"
                                        value={formData.social_links.instagram}
                                        onChange={(e) => handleInputChange(e, 'social_links')}
                                        placeholder="https://instagram.com/..."
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Subscription Tab */}
                        {activeTab === 'subscription' && (
                            <div className="space-y-6">
                                {subscriptions.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-600">
                                        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Aucun abonnement actif</h3>
                                        <p className="text-gray-500 dark:text-gray-400 mt-2">
                                            Contactez l'administrateur pour souscrire à un pack.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-8">
                                        {subscriptions.map((sub) => (
                                            <div key={sub._id} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                                                {/* Pack Info Card */}
                                                <div className="bg-gradient-to-br from-primary-600 to-primary-800 p-6 text-white shadow-lg relative overflow-hidden">
                                                    <div className="absolute top-0 right-0 p-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                                                    <div className="absolute bottom-0 left-0 p-24 bg-black/10 rounded-full -ml-12 -mb-12 blur-xl"></div>

                                                    <div className="relative">
                                                        <div className="flex justify-between items-start mb-6">
                                                            <div>
                                                                <h2 className="text-2xl font-bold mb-1">{sub.pack?.name || 'Pack Inconnu'}</h2>
                                                                <p className="text-primary-100">{sub.pack?.description}</p>
                                                            </div>
                                                            <div className="flex flex-col items-end gap-2">
                                                                <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30 capitalize">
                                                                    {sub.status === 'active' ? 'Actif' : sub.status}
                                                                </span>
                                                                <span className="text-xs text-primary-200">
                                                                    Expire le {new Date(sub.endDate).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-6 mt-8">
                                                            <div>
                                                                <div className="text-primary-200 text-sm mb-1">Durée des offres</div>
                                                                <div className="font-semibold text-lg">{sub.pack?.offerDurationDays || 30} jours</div>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="text-primary-200 text-sm mb-1">Prix</div>
                                                                <div className="font-semibold text-lg">{sub.pack?.price} DA</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Usage Stats for this Subscription */}
                                                <div className="bg-white dark:bg-gray-800 p-6">
                                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Utilisation de cet abonnement</h3>

                                                    <div className="space-y-4">
                                                        {/* Ad Credits */}
                                                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-5">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                                                                        <Briefcase className="w-5 h-5" />
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-medium text-gray-900 dark:text-white">Crédits d'annonces</div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {sub.adCreditsUsed} sur {sub.adCredits} utilisés
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <span className={`text-sm font-medium ${sub.adCreditsUsed >= sub.adCredits
                                                                    ? 'text-red-600 dark:text-red-400'
                                                                    : 'text-green-600 dark:text-green-400'
                                                                    }`}>
                                                                    {sub.adCredits > 0 ? ((sub.adCreditsUsed / sub.adCredits) * 100).toFixed(0) : 0}%
                                                                </span>
                                                            </div>

                                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                                                                <div
                                                                    className={`h-2.5 rounded-full transition-all duration-500 ${sub.adCreditsUsed >= sub.adCredits
                                                                        ? 'bg-red-500'
                                                                        : 'bg-primary-600'
                                                                        }`}
                                                                    style={{ width: `${sub.adCredits > 0 ? Math.min((sub.adCreditsUsed / sub.adCredits) * 100, 100) : 0}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>

                                                        {/* CV Credits */}
                                                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-5">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                                                                        <FileText className="w-5 h-5" />
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-medium text-gray-900 dark:text-white">Crédits CVthèque</div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {sub.cvCreditsUsed} sur {sub.cvCredits} utilisés
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <span className={`text-sm font-medium ${sub.cvCreditsUsed >= sub.cvCredits
                                                                    ? 'text-red-600 dark:text-red-400'
                                                                    : 'text-green-600 dark:text-green-400'
                                                                    }`}>
                                                                    {sub.cvCredits > 0 ? ((sub.cvCreditsUsed / sub.cvCredits) * 100).toFixed(0) : 0}%
                                                                </span>
                                                            </div>

                                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                                                                <div
                                                                    className={`h-2.5 rounded-full transition-all duration-500 ${sub.cvCreditsUsed >= sub.cvCredits
                                                                        ? 'bg-red-500'
                                                                        : 'bg-purple-600'
                                                                        }`}
                                                                    style={{ width: `${sub.cvCredits > 0 ? Math.min((sub.cvCreditsUsed / sub.cvCredits) * 100, 100) : 0}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                                            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between">
                                                                <span className="text-gray-600 dark:text-gray-400">Accès CVthèque</span>
                                                                {sub.pack?.features?.cvtheque ? (
                                                                    <Check className="w-5 h-5 text-green-500" />
                                                                ) : (
                                                                    <X className="w-5 h-5 text-gray-300" />
                                                                )}
                                                            </div>
                                                            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between">
                                                                <span className="text-gray-600 dark:text-gray-400">Publication Missions</span>
                                                                {sub.pack?.features?.missions ? (
                                                                    <Check className="w-5 h-5 text-green-500" />
                                                                ) : (
                                                                    <X className="w-5 h-5 text-gray-300" />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <div className="space-y-6 max-w-md">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Mot de passe actuel
                                    </label>
                                    <input
                                        type="password"
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Nouveau mot de passe
                                    </label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Confirmer le nouveau mot de passe
                                    </label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex justify-end">
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-4 focus:ring-primary-100 disabled:opacity-50 transition-colors"
                            >
                                {saving ? (
                                    <Loader className="w-5 h-5 animate-spin" />
                                ) : (
                                    <Save className="w-5 h-5" />
                                )}
                                Enregistrer
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <ImageCropperModal
                isOpen={isCropperOpen}
                image={tempImageUrl}
                onCropComplete={handleCropComplete}
                onCancel={() => {
                    setIsCropperOpen(false);
                    setTempImageUrl(null);
                }}
            />
        </div>
    );
};

export default Settings;
